import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Button from "components/common/Button";
import LoginButton from "components/user/LoginButton";
import { useAppDispatch, useAppSelector } from "hooks/store";
import {
  toggleCart,
  toggleDrawer,
  toggleGlobalSearchModal,
} from "store/slices/main.slice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import ReservationDialog from "./ReservationDialog";

const ToolBar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const cartLength = useAppSelector(({ main }) => main.cart.length);
  const { settings, companyData } = useAppSelector(({ main }) => main);
  console.log("🚀 - ToolBar - settings:", settings);

  const isMyOrderPage = useMemo(
    () => location.pathname === "/myOrders",
    [location.pathname]
  );

  // === ADDED BY SAMPATH – 2026-01-06 ===
  // Keep header compact on very small devices, prevent overflow, and improve spacing/font scaling.
  // (No logic changes, only className/UI improvements)
  const hasCompanyName = !!settings?.ShowCompanyName && !!companyData?.companyName;
  const hasLocation = !!settings?.DisplayLocationOnCheckout;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Pull Header text colors from settings (same keys used in CompnayProfile.tsx):
  // - headerColor (numeric) and headerTextColor (numeric)
  const convertNumberToHex = (num: number): string => {
    if (num === 0) return "#000";
    return `#${num.toString(16).padStart(6, "0")}`;
  };

  const normalizeHex = (hex: string) => {
    if (!hex) return "#000000";
    let h = hex.trim();
    if (!h.startsWith("#")) h = `#${h}`;
    if (h.length === 4) {
      // #rgb -> #rrggbb
      h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    }
    if (h.length === 7) return h.toLowerCase();
    return "#000000";
  };

  const hexToRgba = (hex: string, alpha = 1) => {
    const h = normalizeHex(hex).replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const headerTextHex = useMemo(() => {
    const s: any = settings as any;

    // Prefer headerTextColor for text; fallback to headerColor (per your request)
    const raw = s?.headerTextColor ?? s?.headerColor ?? null;

    if (raw === null || raw === undefined) return null;

    // raw is numeric in backend (CompnayProfile.tsx saves convertHexToNumber(...))
    const hex = convertNumberToHex(Number(raw));
    return normalizeHex(hex);
  }, [settings]);

  const companyNameStyle = useMemo(() => {
    if (!headerTextHex) return undefined;
    return { color: headerTextHex };
  }, [headerTextHex]);

  const locationStyle = useMemo(() => {
    if (!headerTextHex) return undefined;
    // keep same "soft" look using opacity (matches your transparency requirement)
    return { color: hexToRgba(headerTextHex, 0.9) };
  }, [headerTextHex]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-02-02 ===
  // Premium / refined font at the very top (toolbar area).
  // Applied as a single style on the root toolbar so all header text inherits it.
  const premiumHeaderFont = useMemo(
    () =>
      ({
        fontFamily:
          "Manrope, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      } as React.CSSProperties),
    []
  );
  // === END SAMPATH ===

  return (
    <div
      // === ADDED BY SAMPATH – 2026-01-29 ===
      // IMPORTANT: Mark toolbar so HorizontalBar.tsx can detect toolbar height correctly.
      // Also keep toolbar sticky while product list scrolls (Cactus Club behavior).
      data-dcs-toolbar="true"
      className="
        sticky top-0
        h-[56px] sm:h-[60px]
        px-3 sm:px-4 md:px-6 lg:px-8
        flex items-center justify-between
        shadow-[0px_10px_10px_5px_#00000024]
        z-[80] bg-headerbgcolor
        backdrop-blur-md
      "
      // === END SAMPATH**
      // === ADDED BY SAMPATH – 2026-02-02 ===
      // Refined premium font for the top header area.
      style={premiumHeaderFont}
      // === END SAMPATH ===
    >
      {/* Left Side */}
      <div className="flex items-center min-w-0 flex-1">
        {/* Mobile Menu Icon */}
        <div className="md:hidden shrink-0" onClick={() => dispatch(toggleDrawer())}>
          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* Bigger tap target + consistent sizing */}
          <div className="h-10 w-10 -ml-2 flex items-center justify-center rounded-lg active:scale-[0.98]">
            <MenuIcon className="text-light-text cursor-pointer" />
          </div>
          {/* === END SAMPATH === */}
        </div>

        {/* Back Arrow for 'My Order' page only */}
        {isMyOrderPage && (
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Wrap icon for better mobile tap target
          <div className="shrink-0">
            <div className="h-10 w-10 flex items-center justify-center rounded-lg active:scale-[0.98]">
              <ArrowBackIcon
                className="!text-white cursor-pointer"
                onClick={() => navigate(-1)}
              />
            </div>
          </div>
          // === END SAMPATH ===
        )}

        {/* Company Logo or Admin Icon */}
        {settings?.showLogo !== 0 ? (
          <div
            onDoubleClick={() => {
              window.location.href = "/admin/setting/time";
            }}
            className="text-white px-1.5 relative shrink-0"
            onClick={() => {
              // debugger;
              // console.log("Helllo");
              //ewrfdfer
            }}
          >
            <img
              src={companyData?.logoUrl}
              alt=""
              // === ADDED BY SAMPATH – 2026-01-06 ===
              // Prevent logo from forcing wide layout on mobile
              className="h-9 w-9 sm:h-11 sm:w-11 md:h-12 md:w-12 rounded-md mr-2 md:mr-3 object-contain"
              // === END SAMPATH ===
            />
          </div>
        ) : (
          <div
            onDoubleClick={() => {
              window.location.href = "/admin/setting/time";
            }}
            className="text-white px-1.5 relative shrink-0"
          >
            {/* === ADDED BY SAMPATH – 2026-01-06 === */}
            {/* Keep icon aligned and not oversized */}
            <AdminPanelSettingsIcon className="!text-white mr-2 cursor-pointer !text-[22px] sm:!text-[24px]" />
            {/* === END SAMPATH === */}
          </div>
        )}

        {/* Company Text */}
        <div className="min-w-0 flex-1">
          {/* Company Name */}
          {!!settings.ShowCompanyName && !!companyData?.companyName && (
            <a href="/" className="block min-w-0">
              <h1
                // === ADDED BY SAMPATH – 2026-01-29 ===
                // Use Header color settings for company name text (from settings.headerTextColor/headerColor)
                style={companyNameStyle}
                className="
                  text-[14px] sm:text-[16px] md:text-[18px]
                  font-semibold md:font-bold
                  leading-tight
                  truncate
                "
                // === END SAMPATH ===
                // === ADDED BY SAMPATH – 2026-02-02 ===
                // Less “blocky” look: tighter tracking + optical smoothing.
                // (No logic change, only typography refinement)
                // === END SAMPATH ===
              >
                {companyData?.companyName}
              </h1>
            </a>
          )}

          {!!settings.DisplayLocationOnCheckout && (
            <p
              // === ADDED BY SAMPATH – 2026-01-29 ===
              // Use Header color settings for location text with soft transparency
              style={locationStyle}
              className="
                text-[11px] sm:text-[12px]
                leading-tight
                truncate
              "
              // === END SAMPATH ===
              title={`${companyData?.address1 ?? ""} ${companyData?.address2 ?? ""}`}
            >
              {companyData?.address1} {companyData?.address2}
            </p>
          )}

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* If neither name nor location is shown, keep height stable (prevents jumpy layout). */}
          {!hasCompanyName && !hasLocation && <div className="h-[2px]" />}
          {/* === END SAMPATH === */}
        </div>
      </div>

      {/* Right Side Actions */}
      <div
        // === ADDED BY SAMPATH – 2026-01-06 ===
        // Prevent buttons from stretching + better spacing on small screens
        className="flex items-center gap-1.5 sm:gap-2 shrink-0"
        // === END SAMPATH ===
      >
        <Button
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Square icon buttons: consistent size, less empty space, better mobile tap target
          className="bg-white !text-dark-text !px-0 !min-w-[40px] !h-[40px] sm:!min-w-[42px] sm:!h-[42px] rounded-xl relative"
          // === END SAMPATH ===
          onClick={() => dispatch(toggleGlobalSearchModal())}
        >
          <SearchIcon className="!text-black cursor-pointer !text-[20px]" />
        </Button>

        {/* Home Button for 'My Order' page only */}
        {isMyOrderPage && (
          <Button
            // === ADDED BY SAMPATH – 2026-01-06 ===
            className="bg-white !text-dark-text !px-0 !min-w-[40px] !h-[40px] sm:!min-w-[42px] sm:!h-[42px] rounded-xl relative"
            // === END SAMPATH ===
            onClick={() => navigate("/")}
          >
            <HomeIcon className="!text-black cursor-pointer !text-[20px]" />
          </Button>
        )}

        {/* Login Button */}
        {/* === ADDED BY SAMPATH – 2026-01-06 === */}
        {/* Keep LoginButton aligned (doesn't change its internal styling) */}
        <div className="shrink-0">
          <LoginButton />
        </div>
        {/* === END SAMPATH === */}

        {!!settings.CloseReservationOrder &&
          !!settings.ReservationOrderOptions && <ReservationDialog />}

        {/* Shopping Cart Button */}
        <Button
          // === ADDED BY SAMPATH – 2026-01-06 ===
          className="bg-white !text-dark-text !px-0 !min-w-[40px] !h-[40px] sm:!min-w-[42px] sm:!h-[42px] rounded-xl relative"
          // === END SAMPATH ===
          onClick={() => dispatch(toggleCart())}
        >
          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* Consistent icon sizing */}
          <ShoppingCartIcon className="!text-[20px] sm:!text-[21px]" />
          {/* === END SAMPATH === */}
          {!!cartLength && (
            <div
              // === ADDED BY SAMPATH – 2026-01-06 ===
              // Badge: tighter + centered + uses theme secondary but softened
              className="
                bg-secondary text-light-text
                text-[9px] sm:text-[10px]
                absolute
                h-[16px] w-[16px] sm:h-[18px] sm:w-[18px]
                rounded-full
                flex justify-center items-center
                -top-[4px] -right-[4px] sm:-top-[6px] sm:-right-[6px]
                shadow-sm
              "
              // === END SAMPATH ===
            >
              {cartLength}
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ToolBar;
